import { FocusEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { churchOrgTypes, Household, schoolOrgTypes } from '../../../../common/src/model.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { capitalize, isSameEmail, isValidEmail, normalizeEmail } from '../../../../common/src/util.ts';
import { synchronizeOrgsWithServer, useCurrentOrg } from '../../data/use_current_org.tsx';
import {
  emailStopsSigningIn,
  memberCarriesEmail,
  synchronizeHouseholdWithServer,
  useHousehold,
} from '../../data/use_household.tsx';
import { alert, confirm } from '../../util/confirm.tsx';
import { ForwardArrow } from '../../util/forward_arrow.tsx';
import { useOnline } from '../../util/use_online.tsx';
import { useAllSongLists } from '../admin/org/use_all_song_lists.ts';
import { ChooseChurchPage } from '../choose_church_page.tsx';
import { ChooseSchoolPage } from '../choose_school_page.tsx';
import { ChooseUser } from '../onboarding/choose_user.tsx';
import '../settings_page.css';
import { DialogPage, SubViewOverlay } from './dialog_page.tsx';
import { HouseholdSchedule } from './household_schedule.tsx';
import './shared.css';

enum HouseholdSubView {
  ChooseChurch = 'ChooseChurch',
  ChooseSchool = 'ChooseSchool',
  Schedule = 'Schedule',
  EditMembers = 'EditMembers',
}

export const HouseholdPage = () => {
  const [subView, setSubView] = useState<HouseholdSubView | undefined>();
  const { household, setHousehold } = useHousehold();
  const { currentOrg, householdOrgs } = useCurrentOrg();
  const currentChurch = householdOrgs.filter(({ type }) => churchOrgTypes.includes(type))[0];
  const currentSchool = householdOrgs.filter(({ type }) => schoolOrgTypes.includes(type))[0];
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const allSongLists = useAllSongLists();
  const isOnline = useOnline();
  const navigate = useNavigate();
  const { schedule } = household?.surveyResponses ?? {};

  useEffect(() => {
    void synchronizeHouseholdWithServer({ force: true });
    void synchronizeOrgsWithServer({ force: true });
  }, []);

  const onChooseOrg = useCallback(
    async ({
      nextSubView,
      plannerName,
      orgTypeNamePlural,
    }: {
      nextSubView: HouseholdSubView;
      plannerName: string;
      orgTypeNamePlural: string;
    }) => {
      if (!isOnline) {
        void alert({ confirmation: <span>An internet connection is required to switch organizations.</span> });
        return;
      }
      if (
        (!currentOrg?.updates && !allSongLists.containsUpdates) ||
        (await confirm({
          confirmation: (
            <span>
              Unsaved {plannerName} changes will be lost if you switch {orgTypeNamePlural}. Continue?
            </span>
          ),
        }))
      ) {
        setSubView(nextSubView);
      }
    },
    [allSongLists, currentOrg?.updates, isOnline],
  );

  const onShowChooseChurch = useCallback(async () => {
    await onChooseOrg({
      nextSubView: HouseholdSubView.ChooseChurch,
      plannerName: 'church liturgy',
      orgTypeNamePlural: 'churches',
    });
  }, [onChooseOrg]);

  const onShowChooseSchool = useCallback(async () => {
    await onChooseOrg({
      nextSubView: HouseholdSubView.ChooseSchool,
      plannerName: 'school planner',
      orgTypeNamePlural: 'schools',
    });
  }, [onChooseOrg]);

  const onShowSchedule = useCallback(() => {
    setSubView(HouseholdSubView.Schedule);
  }, []);

  const onShowEditMembers = useCallback(() => {
    setSubView(HouseholdSubView.EditMembers);
  }, []);

  const closeSubView = useCallback(() => {
    setSubView(undefined);
  }, []);

  const onLastNameBlur = useCallback(
    async (event: FocusEvent<HTMLInputElement>) => {
      if (!household) {
        return;
      }
      const newHousehold: Household = { ...household };
      newHousehold.familyLastName = event.target.value;
      await setHousehold(newHousehold);
    },
    [household, setHousehold],
  );

  const onHouseholdEmailBlur = useCallback(
    async (event: FocusEvent<HTMLInputElement>) => {
      if (!household) {
        return;
      }
      const newEmail = event.target.value;
      if (!isValidEmail(newEmail)) {
        void alert({
          confirmation: <span>&quot;{newEmail}&quot; is not a valid email address</span>,
        });
        event.target.value = household.householdEmail;
        return;
      }
      const emailChanged = !isSameEmail(newEmail, household.householdEmail);
      // A member's email cannot become the household's: the household's current email would then be carried by
      // nobody and sign in nowhere, as the server says too (GitHub issue #4104). The members are at hand here.
      if (emailChanged && memberCarriesEmail(household, newEmail)) {
        void alert({
          confirmation: (
            <span>
              The email {newEmail.trim()} belongs to a member of this household; remove it from the member first
            </span>
          ),
        });
        event.target.value = household.householdEmail;
        return;
      }
      // An email that already signs in to another household (its own or a member's) is refused here, before the
      // household changes: the offline sync retries a whole-household save the server refuses for ever. Offline,
      // the save goes ahead and the server refuses it (GitHub issue #4104).
      if (emailChanged && (await signsInElsewhere(newEmail, household.token))) {
        void alert({ confirmation: <span>That email already belongs to another household</span> });
        event.target.value = household.householdEmail;
        return;
      }
      // The old household email stops signing in unless a member carries it; no sign-in email disappears without
      // the family choosing it (GitHub issue #4104)
      const oldEmail = normalizeEmail(household.householdEmail);
      if (
        emailChanged &&
        oldEmail &&
        !memberCarriesEmail(household, oldEmail) &&
        !(await confirm({ confirmation: <span>{emailStopsSigningIn(oldEmail, newEmail.trim())}</span> }))
      ) {
        event.target.value = household.householdEmail;
        return;
      }
      const newHousehold: Household = { ...household };
      newHousehold.householdEmail = newEmail;
      await setHousehold(newHousehold);
    },
    [household, setHousehold],
  );

  return (
    <>
      <DialogPage title="Household">
        <div className="settingsContent">
          <div className="settingsBox">
            <table className="settings">
              <tbody>
                <tr key="last name" className="setting arrow">
                  <td className="settingName">Last Name</td>
                  <td className="currentValue">
                    <InvisibleInput
                      type="text"
                      ref={nameInputRef}
                      defaultValue={household?.familyLastName}
                      onBlur={onLastNameBlur}
                      onKeyUp={(event) => {
                        if ((event.key === 'Enter' || event.key === 'Escape') && nameInputRef.current) {
                          nameInputRef.current.blur();
                        }
                      }}
                    />
                  </td>
                  <td className="settingAction"></td>
                </tr>
                <tr key="email" className="setting arrow">
                  <td className="settingName">Email</td>
                  <td className="currentValue">
                    <InvisibleInput
                      type="email"
                      ref={emailInputRef}
                      defaultValue={household?.householdEmail}
                      onBlur={onHouseholdEmailBlur}
                      onKeyUp={(event) => {
                        if ((event.key === 'Enter' || event.key === 'Escape') && emailInputRef.current) {
                          emailInputRef.current.blur();
                        }
                      }}
                    />
                  </td>
                  <td className="settingAction"></td>
                </tr>
                <tr key="church" onClick={onShowChooseChurch} className="setting arrow">
                  <td className="settingName">Church</td>
                  <td className="currentValueEllipse">{currentChurch?.name ?? 'No Church'}</td>
                  <td>
                    <ForwardArrow />
                  </td>
                </tr>
                <tr key="school" onClick={onShowChooseSchool} className="setting arrow">
                  <td className="settingName">School</td>
                  <td className="currentValueEllipse">{currentSchool?.name ?? 'No School'}</td>
                  <td>
                    <ForwardArrow />
                  </td>
                </tr>
                {household?.surveyResponses && (
                  <tr key="schedule" onClick={onShowSchedule} className="setting arrow">
                    <td className="settingName">Schedule</td>
                    <td className="currentValueEllipse">
                      {schedule ? schedule.map((entry) => capitalize(entry)).join('/') : ''}
                    </td>
                    <td>
                      <ForwardArrow />
                    </td>
                  </tr>
                )}
                <tr key="edit members" onClick={onShowEditMembers} className="setting arrow">
                  <td className="settingName">Edit Members</td>
                  <td className="currentValueEllipse"></td>
                  <td>
                    <ForwardArrow />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </DialogPage>
      {household && subView === HouseholdSubView.ChooseChurch && (
        <SubViewOverlay onClose={closeSubView}>
          {(close) => <ChooseChurchPage visible={true} onSettingChanged={close} onBack={close} />}
        </SubViewOverlay>
      )}
      {household && subView === HouseholdSubView.ChooseSchool && (
        <SubViewOverlay onClose={closeSubView}>
          {(close) => <ChooseSchoolPage visible={true} onSettingChanged={close} onBack={close} />}
        </SubViewOverlay>
      )}
      {subView === HouseholdSubView.Schedule && (
        <SubViewOverlay onClose={closeSubView}>
          {(close) => <HouseholdSchedule visible={true} onBack={close} />}
        </SubViewOverlay>
      )}
      {household && subView === HouseholdSubView.EditMembers && (
        <SubViewOverlay onClose={closeSubView}>
          {(close) => <ChooseUser onBack={close} navigate={navigate} startInEditMode={true} prompt="Edit Members" />}
        </SubViewOverlay>
      )}
    </>
  );
};

const InvisibleInput = styled.input`
  width: 100%;
  height: 23px;
  text-align: right;
  border: none;
  outline: none;
  color: var(--color-text);
  background-color: var(--color-background);
  overflow: hidden;
  font-size: 16px;
  text-overflow: ellipsis;
`;

// whether an email signs in to a household other than householdToken's; a failed lookup answers no
async function signsInElsewhere(email: string, householdToken: string): Promise<boolean> {
  try {
    const signsInTo = await server_api.getHouseholdToken({ householdEmail: email });
    return Boolean(signsInTo) && signsInTo !== householdToken;
  } catch {
    return false;
  }
}
